import type { ApiResult } from "@/types";
import { createRequestKey, OPERATION_STORAGE_NOTICE, validOperationJobId, validRequestKey, type OperationCheckpoint, type OperationKind, type OperationStore } from "./operationReceipt.ts";

export type OperationAccepted = {
  version: 1;
  acceptance: "accepted";
  kind: OperationKind;
  job_id: string;
  status: "queued" | "running" | "done" | "error" | "unavailable" | "deleted";
  result_path: string | null;
  charged?: number;
};
export type OperationSnapshot = {
  phase: "idle" | "submitting" | "checking" | "unknown" | "accepted" | "rejected" | "blocked";
  checkpoint: OperationCheckpoint | null;
  accepted: OperationAccepted | null;
  error: { message: string; code?: string } | null;
  notice: string | null;
};
type SubmissionResult = { job_id?: string; status?: string; operation_receipt?: unknown };
type Lookup = (key: string, signal?: AbortSignal) => Promise<ApiResult<unknown>>;
export const operationResultPath = (kind: OperationKind, jobId: string) => kind === "checkup"
  ? `/app/checkup/${encodeURIComponent(jobId)}` : `/app/tools/scan/${encodeURIComponent(jobId)}`;

export function parseOperationAccepted(value: unknown, kind: OperationKind): OperationAccepted | null {
  if (!value || typeof value !== "object") return null;
  const receipt = value as OperationAccepted;
  if (receipt.version !== 1 || receipt.acceptance !== "accepted" || receipt.kind !== kind
      || !validOperationJobId(receipt.job_id)
      || !["queued", "running", "done", "error", "unavailable", "deleted"].includes(receipt.status)
      || (receipt.result_path !== null && receipt.result_path !== operationResultPath(kind, receipt.job_id))) return null;
  return { version: 1, acceptance: "accepted", kind, job_id: receipt.job_id, status: receipt.status,
    result_path: ["deleted", "unavailable"].includes(receipt.status) ? null : receipt.result_path,
    ...(typeof receipt.charged === "number" && Number.isFinite(receipt.charged) && receipt.charged >= 0 ? { charged: receipt.charged } : {}),
  };
}

/** A submitted attempt keeps its original key. Recovery never uploads or creates a key. */
export function createOperationWorkflow(kind: OperationKind, store: OperationStore, lookup: Lookup,
  onChange?: (snapshot: OperationSnapshot) => void, newKey: () => string = createRequestKey) {
  const restored = store.read();
  let snapshot: OperationSnapshot = {
    phase: restored.blocked ? "blocked" : restored.checkpoint?.phase === "accepted" ? "accepted" : restored.checkpoint ? "unknown" : "idle",
    checkpoint: restored.checkpoint, accepted: null, error: null, notice: restored.notice,
  };
  let busy = false;
  let generation = 0;
  let controller: AbortController | null = null;
  const publish = (patch: Partial<OperationSnapshot>) => { snapshot = { ...snapshot, ...patch }; onChange?.(snapshot); };
  const save = (checkpoint: OperationCheckpoint | null) => {
    const ok = store.write(checkpoint);
    if (!ok) publish({ notice: OPERATION_STORAGE_NOTICE });
    return ok;
  };
  const accept = (receipt: OperationAccepted) => {
    const checkpoint = { kind, requestKey: snapshot.checkpoint!.requestKey, phase: "accepted" as const, jobId: receipt.job_id };
    save(checkpoint);
    publish({ phase: "accepted", checkpoint, accepted: receipt, error: null });
  };
  async function reconcile() {
    if (busy || !snapshot.checkpoint || snapshot.phase === "blocked") return;
    busy = true;
    const current = ++generation;
    const checkpoint = snapshot.checkpoint;
    controller = new AbortController();
    publish({ phase: "checking", error: null });
    try {
      const result = await lookup(checkpoint.requestKey, controller.signal);
      if (current !== generation) return;
      const receipt = result.ok ? parseOperationAccepted(result.data, kind) : null;
      if (receipt && (!checkpoint.jobId || checkpoint.jobId === receipt.job_id)) { accept(receipt); return; }
      publish({ phase: checkpoint.phase === "accepted" ? "accepted" : "unknown", error: {
        message: !result.ok && result.code === "receipt_not_observed"
          ? "尚未查到原请求的受理记录，这不代表未受理。请稍后核对原请求；不会重新上传或扣费。"
          : result.ok ? "原请求返回的记录无法核对，请保留请求编号并检查已有任务；不会重新提交。"
          : `${result.error}。暂时无法确认原请求，稍后可继续核对；不会重新上传或扣费。`,
        code: !result.ok ? result.code : "operation_response_mismatch",
      } });
    } catch {
      if (current === generation) publish({ phase: checkpoint.phase === "accepted" ? "accepted" : "unknown",
        error: { message: "核对原请求中断，请稍后再查；不会重新上传或扣费。" } });
    } finally { if (current === generation) busy = false; }
  }
  async function submit(send: (requestKey: string) => Promise<ApiResult<SubmissionResult>>) {
    if (busy || !["idle", "rejected"].includes(snapshot.phase)) return;
    busy = true;
    const current = ++generation;
    const previous = snapshot.checkpoint;
    let key: string;
    try { key = newKey(); if (!validRequestKey(key)) throw new Error("invalid key"); }
    catch {
      publish({ phase: "rejected", error: { message: "浏览器无法生成请求编号，本次尚未提交。请使用支持安全随机数的浏览器。" } });
      busy = false; return;
    }
    const checkpoint: OperationCheckpoint = { kind, requestKey: key, phase: "unknown" };
    if (!save(checkpoint)) {
      save(previous);
      publish({ phase: "rejected", error: { message: "浏览器无法保存任务恢复记录，本次尚未提交。请允许此网站使用浏览器存储后再试。", code: "operation_storage_unavailable" } });
      busy = false; return;
    }
    publish({ phase: "submitting", checkpoint, accepted: null, error: null, notice: null });
    let needsLookup = false;
    try {
      const result = await send(key);
      if (current !== generation) return;
      if (result.ok) {
        const data = result.data;
        const receipt = data?.operation_receipt !== undefined ? parseOperationAccepted(data.operation_receipt, kind)
          : validOperationJobId(data?.job_id) ? { version: 1 as const, acceptance: "accepted" as const, kind,
            job_id: data.job_id, status: "queued" as const, result_path: operationResultPath(kind, data.job_id) } : null;
        if (receipt) { accept(receipt); return; }
      } else if ((result.status !== undefined && [400, 401, 402, 403, 413, 422].includes(result.status)
          && result.code !== "request_key_conflict") || (result.status === 404
            && result.code === "project_unavailable" && result.acceptance === "rejected")) {
        // Contract: initial keyed POST rejects these before acceptance or charging.
        save(null);
        publish({ phase: "rejected", checkpoint: null, error: { message: result.error, code: result.code } });
        return;
      }
      publish({ phase: "unknown", error: { message: "提交响应未能确认，正在核对原请求。不会重新上传或生成新请求。" } });
      needsLookup = true;
    } catch {
      if (current === generation) {
        publish({ phase: "unknown", error: { message: "提交响应中断，正在核对原请求；不会重新上传。" } });
        needsLookup = true;
      }
    } finally {
      if (current === generation) {
        busy = false;
        if (needsLookup) await reconcile();
      }
    }
  }
  return {
    getSnapshot: () => snapshot, submit, reconcile,
    beginNew() {
      if (busy || !["accepted", "rejected"].includes(snapshot.phase)) return false;
      if (!save(null)) { save(snapshot.checkpoint); return false; }
      publish({ phase: "idle", checkpoint: null, accepted: null, error: null, notice: null });
      return true;
    },
    stop() {
      generation++; controller?.abort(); busy = false;
      if (snapshot.phase === "submitting" || snapshot.phase === "checking") snapshot = {
        ...snapshot, phase: snapshot.checkpoint?.phase === "accepted" ? "accepted" : "unknown",
      };
    },
  };
}
